import { useNavigate } from "react-router-dom";
import { isKiosk } from "@/lib/kiosk";
import { useCart } from "@/context/CartContext";
import { fileUrl } from "@/lib/api";
import { serviceLabel } from "@/lib/service";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Trash2, Minus, Plus } from "lucide-react";

export function CartDrawer() {
  const { items, removeItem, updateQty, subtotal, drawerOpen, setDrawerOpen } = useCart();
  const navigate = useNavigate();
  return (
    <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
      <SheetContent className="bg-[#12141a] border-zinc-800 text-zinc-100 w-full sm:max-w-md flex flex-col" data-testid="cart-drawer">
        <SheetHeader>
          <SheetTitle className="font-display text-2xl text-zinc-100">Your Cart</SheetTitle>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto py-4 space-y-3">
          {items.length === 0 && (
            <p className="text-zinc-500 text-sm mt-10 text-center font-mono-p">Cart is empty. Add designs or a gang sheet.</p>
          )}
          {items.map((it) => (
            <div key={it.id} className="flex gap-3 bg-[#1e222d] border border-zinc-800 rounded-xl p-3" data-testid="cart-item">
              <div className="w-16 h-16 rounded-lg checker-light shrink-0 overflow-hidden flex items-center justify-center">
                {(it.image_url || it.preview_url) && (
                  <img src={fileUrl(it.preview_url || it.image_url)} alt="" className="max-w-full max-h-full object-contain" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold truncate">{it.name}</p>
                <p className="text-xs text-zinc-500 font-mono-p">
                  {it.kind === "apparel" ? `${it.color} · ${it.size} · ${(it.placements && it.placements.length) ? it.placements.join(", ") : it.placement}` : it.kind === "blank" ? `Blank · ${[it.color, it.size].filter(Boolean).join(" · ") || "no design"}` : it.kind === "label" ? `Labels${it.rush ? " · Rush" : ""}` : it.kind === "design_request" ? "Custom design" : it.kind === "manual" ? "Custom item" : `${it.sheet_name || ""}${serviceLabel(it) ? ` · ${serviceLabel(it)}` : ""}`}
                </p>
                <div className="flex items-center justify-between mt-2">
                  <div className="flex items-center gap-2">
                    <button onClick={() => updateQty(it.id, it.quantity - 1)} className="w-6 h-6 rounded bg-zinc-800 flex items-center justify-center hover:bg-zinc-700" data-testid="cart-qty-minus"><Minus size={12} /></button>
                    <span className="text-sm w-6 text-center font-mono-p">{it.quantity}</span>
                    <button onClick={() => updateQty(it.id, it.quantity + 1)} className="w-6 h-6 rounded bg-zinc-800 flex items-center justify-center hover:bg-zinc-700" data-testid="cart-qty-plus"><Plus size={12} /></button>
                  </div>
                  <span className="text-sm font-bold text-[#ff6a13] font-mono-p">${(it.unit_price * it.quantity).toFixed(2)}</span>
                </div>
              </div>
              <button onClick={() => removeItem(it.id)} className="text-zinc-600 hover:text-red-400 self-start" data-testid="cart-remove"><Trash2 size={16} /></button>
            </div>
          ))}
        </div>
        <div className="border-t border-zinc-800 pt-4 space-y-3">
          <div className="flex justify-between text-sm">
            <span className="text-zinc-400">Subtotal</span>
            <span className="font-bold text-lg font-mono-p" data-testid="cart-subtotal">${subtotal.toFixed(2)}</span>
          </div>
          <Button
            disabled={items.length === 0}
            data-testid="cart-checkout-btn"
            onClick={() => { setDrawerOpen(false); navigate(isKiosk() ? "/vendor/checkout" : "/checkout"); }}
            className="w-full bg-[#ff6a13] hover:bg-[#ff8a3d] text-white font-semibold rounded-full h-11 active:scale-95 transition-transform">
            Checkout
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
